import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Camera, User, Mail, Lock, Shield, Key, Bell, Coins, Palette } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { fetchNotificationPreferences, upsertNotificationPreferences } from "@/lib/notifications";
import type { NotificationPreference } from "@/lib/types";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { SUPPORTED_CURRENCIES, DEFAULT_CURRENCY, formatMoney } from "@/lib/currency";
import { useBrand } from "@/hooks/use-brand";

export const Route = createFileRoute("/app/account")({ component: AccountPage });

function AccountPage() {
  const { profile, refresh } = useAuth();
  const activeBrand = useBrand();
  const [fullName, setFullName] = useState(profile?.full_name ?? "");
  const [currency, setCurrency] = useState<string>(profile?.currency ?? DEFAULT_CURRENCY);
  const [savingCurrency, setSavingCurrency] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [notificationPrefs, setNotificationPrefs] = useState<NotificationPreference | null>(null);
  const [savingPrefs, setSavingPrefs] = useState(false);

  // Event preferences state
  const [emailJobCreated, setEmailJobCreated] = useState(true);
  const [emailJobPrintStarted, setEmailJobPrintStarted] = useState(true);
  const [emailJobReady, setEmailJobReady] = useState(true);
  const [emailJobOnHold, setEmailJobOnHold] = useState(true);
  const [inappJobCreated, setInappJobCreated] = useState(true);
  const [inappJobPrintStarted, setInappJobPrintStarted] = useState(true);
  const [inappJobReady, setInappJobReady] = useState(true);
  const [inappJobOnHold, setInappJobOnHold] = useState(true);

  useEffect(() => {
    if (profile?.id) {
      fetchNotificationPreferences(profile.id).then(setNotificationPrefs);
    }
  }, [profile?.id]);

  useEffect(() => {
    if (profile?.currency) setCurrency(profile.currency);
  }, [profile?.currency]);

  const handleSaveCurrency = async () => {
    if (!profile?.id) return;
    setSavingCurrency(true);
    try {
      const { error } = await supabase.from("profiles").update({ currency }).eq("id", profile.id);
      if (error) throw error;
      await refresh();
      toast.success("Currency updated");
    } catch (e) {
      toast.error((e as Error).message ?? "Failed to update currency");
    } finally {
      setSavingCurrency(false);
    }
  };

  const handleSaveName = async () => {
    setSaving(true);
    // TODO: Call Supabase to update profile.full_name
    toast.success("Name updated successfully");
    setSaving(false);
  };

  const handleSaveNotificationPrefs = async () => {
    if (!profile?.id) return;
    setSavingPrefs(true);
    try {
      await upsertNotificationPreferences(profile.id, {
        is_enabled: true,
        category: "job_updates",
        urgency: "normal",
        digest_frequency: "immediate",
        delivery_method: "inapp",
        email_job_created: emailJobCreated,
        email_job_print_started: emailJobPrintStarted,
        email_job_ready: emailJobReady,
        email_job_on_hold: emailJobOnHold,
        inapp_job_created: inappJobCreated,
        inapp_job_print_started: inappJobPrintStarted,
        inapp_job_ready: inappJobReady,
        inapp_job_on_hold: inappJobOnHold,
      });
      toast.success("Notification preferences updated");
    } catch (error) {
      toast.error("Failed to update notification preferences");
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleChangeEmail = () => {
    // TODO: Call Supabase auth update email API
    setShowEmailChange(false);
    toast.success(
      "We've sent a confirmation link to your new email. Your sign-in email will update once it's confirmed.",
    );
  };

  const handlePasswordReset = () => {
    // TODO: Call Supabase password reset
    toast.info(
      "Use the 'Forgot password' link on the login page; we'll send a reset email to your account.",
    );
  };

  const domain = profile?.email?.split("@")[1] ?? "";

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Your account</h1>
        <p className="mt-2 text-muted-foreground">Manage your profile and sign-in details.</p>
      </div>

      {/* Profile & photo */}
      <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <User className="h-5 w-5" /> Profile & photo
        </h2>
        <div className="mt-6 flex flex-col gap-6 sm:flex-row sm:items-start">
          <div className="flex-shrink-0">
            <div className="relative h-24 w-24 rounded-full bg-gradient-hero flex items-center justify-center text-primary-foreground text-2xl font-bold">
              {profile?.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt="Avatar"
                  className="h-full w-full rounded-full object-cover"
                />
              ) : (
                (profile?.full_name ?? profile?.email ?? "?").slice(0, 1).toUpperCase()
              )}
            </div>
            <Button size="sm" variant="outline" className="mt-2 w-full rounded-full" asChild>
              <label className="cursor-pointer">
                <Camera className="h-4 w-4 mr-2" /> Change photo
                <input type="file" className="hidden" accept="image/*" />
              </label>
            </Button>
          </div>
          <div className="flex-1 space-y-4">
            <div className="space-y-1.5">
              <Label>Full name</Label>
              <Input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="rounded-2xl"
              />
            </div>
            <Button
              onClick={handleSaveName}
              disabled={saving}
              className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
            >
              {saving ? "Saving..." : "Save name"}
            </Button>
          </div>
        </div>
      </div>

      {/* Currency preference */}
      <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Coins className="h-5 w-5" /> Currency
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Prices across the shop, cart and checkout will display in this currency.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
          <div>
            <Label htmlFor="currency-select">Display currency</Label>
            <select
              id="currency-select"
              className="mt-1.5 w-full rounded-2xl border border-border bg-background px-3 py-2 text-sm"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            >
              {SUPPORTED_CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div className="text-sm text-muted-foreground tabular-nums">
            Preview: {formatMoney(1234.5, currency)}
          </div>
          <Button
            onClick={handleSaveCurrency}
            disabled={savingCurrency || currency === (profile?.currency ?? DEFAULT_CURRENCY)}
            className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
          >
            {savingCurrency ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      {/* Email & security */}
      <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Mail className="h-5 w-5" /> Email & security
        </h2>
        <div className="mt-6 space-y-6">
          <div>
            <Label>Email</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <Input value={profile?.email ?? ""} readOnly className="rounded-2xl bg-muted" />
              {!showEmailChange && (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  onClick={() => setShowEmailChange(true)}
                >
                  Change email
                </Button>
              )}
            </div>
            {showEmailChange && (
              <div className="mt-4 space-y-3 rounded-2xl border border-border p-4 bg-muted/30">
                <Input
                  type="email"
                  placeholder="Enter new email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="rounded-2xl"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={handleChangeEmail}
                    className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
                  >
                    Send confirmation
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowEmailChange(false)}
                    className="rounded-full"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <Label>Password</Label>
              <p className="mt-1 text-sm text-muted-foreground">
                Change your password to keep your account secure.
              </p>
            </div>
            <Button variant="outline" onClick={handlePasswordReset} className="rounded-full">
              <Key className="h-4 w-4 mr-2" /> Request password reset
            </Button>
          </div>
        </div>
      </div>

      {/* 2FA placeholder */}
      <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Shield className="h-5 w-5" /> Two-factor authentication
        </h2>
        <div className="mt-4">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-600 border border-amber-500/20">
            Coming soon
          </span>
          <p className="mt-3 text-sm text-muted-foreground">
            2FA is not enabled yet. This is a planned enhancement for {activeBrand.name}. When
            available, you'll be able to add an extra code from an authenticator app.
          </p>
        </div>
      </div>

      {/* Notification preferences */}
      <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Bell className="h-5 w-5" /> Notification preferences
        </h2>
        <div className="mt-4 space-y-4">
          <p className="text-sm text-muted-foreground">
            Manage how you receive notifications about your jobs and account updates.
          </p>
          <div className="rounded-2xl border border-border bg-muted/30 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-4 py-2 text-left font-medium">Event</th>
                  <th className="px-4 py-2 text-center font-medium">In-app</th>
                  <th className="px-4 py-2 text-center font-medium">Email</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="px-4 py-3">
                    <div className="font-medium">Job created</div>
                    <div className="text-xs text-muted-foreground">When your job is submitted</div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Switch checked={inappJobCreated} onCheckedChange={setInappJobCreated} />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Switch checked={emailJobCreated} onCheckedChange={setEmailJobCreated} />
                  </td>
                </tr>
                <tr className="border-b border-border">
                  <td className="px-4 py-3">
                    <div className="font-medium">Print started</div>
                    <div className="text-xs text-muted-foreground">When production begins</div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Switch
                      checked={inappJobPrintStarted}
                      onCheckedChange={setInappJobPrintStarted}
                    />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Switch
                      checked={emailJobPrintStarted}
                      onCheckedChange={setEmailJobPrintStarted}
                    />
                  </td>
                </tr>
                <tr className="border-b border-border">
                  <td className="px-4 py-3">
                    <div className="font-medium">Ready for collection</div>
                    <div className="text-xs text-muted-foreground">When your job is complete</div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Switch checked={inappJobReady} onCheckedChange={setInappJobReady} />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Switch checked={emailJobReady} onCheckedChange={setEmailJobReady} />
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3">
                    <div className="font-medium">Job on hold</div>
                    <div className="text-xs text-muted-foreground">
                      When your job needs attention
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Switch checked={inappJobOnHold} onCheckedChange={setInappJobOnHold} />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Switch checked={emailJobOnHold} onCheckedChange={setEmailJobOnHold} />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <Button
            onClick={handleSaveNotificationPrefs}
            disabled={savingPrefs}
            className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
          >
            {savingPrefs ? "Saving..." : "Save preferences"}
          </Button>
        </div>
      </div>

      {/* White-label branding — editable by the customer themselves */}
      {profile?.white_label_enabled && <WhiteLabelBrandingCard />}

      {/* Team access - customers only */}
      {profile?.role === "customer" && (
        <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <User className="h-5 w-5" /> Your team on {activeBrand.name}
          </h2>
          <p className="mt-3 text-sm text-muted-foreground">
            Invite colleagues from your company so they can upload artwork and track jobs too.
          </p>
          <Button
            asChild
            className="mt-4 rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
          >
            <Link to="/app/customer/team">Manage team access</Link>
          </Button>
        </div>
      )}
    </div>
  );
}

function WhiteLabelBrandingCard() {
  const { profile, refresh } = useAuth();
  const [wlName, setWlName] = useState(profile?.white_label_name ?? "");
  const [wlTagline, setWlTagline] = useState(profile?.white_label_tagline ?? "");
  const [wlLogoUrl, setWlLogoUrl] = useState(profile?.white_label_logo_url ?? "");
  const [wlPrimary, setWlPrimary] = useState(profile?.white_label_primary_color ?? "#3b82f6");
  const [wlAccent, setWlAccent] = useState(profile?.white_label_accent_color ?? "#8b5cf6");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!profile?.id) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          white_label_name: wlName.trim() || null,
          white_label_tagline: wlTagline.trim() || null,
          white_label_logo_url: wlLogoUrl.trim() || null,
          white_label_primary_color: wlPrimary || null,
          white_label_accent_color: wlAccent || null,
        } as any)
        .eq("id", profile.id);
      if (error) throw error;
      await refresh();
      toast.success("Branding saved");
    } catch (e) {
      toast.error((e as Error).message ?? "Failed to save branding");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-3xl border border-emerald-500/30 bg-emerald-500/5 p-6">
      <h2 className="text-lg font-semibold flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
        <Palette className="h-5 w-5" /> Your branding
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Customise how your portal looks — name, logo, and colours shown throughout the app.
      </p>

      <div className="mt-5 space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Brand name</Label>
            <Input
              value={wlName}
              onChange={(e) => setWlName(e.target.value)}
              placeholder="Your company name"
              className="rounded-2xl"
            />
          </div>
          <div className="space-y-1.5">
            <Label>
              Tagline <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Input
              value={wlTagline}
              onChange={(e) => setWlTagline(e.target.value)}
              placeholder="Short strapline"
              className="rounded-2xl"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>
            Logo URL <span className="text-muted-foreground font-normal">(optional)</span>
          </Label>
          <Input
            value={wlLogoUrl}
            onChange={(e) => setWlLogoUrl(e.target.value)}
            placeholder="https://example.com/logo.png"
            className="rounded-2xl"
          />
          {wlLogoUrl && (
            <img
              src={wlLogoUrl}
              alt="Logo preview"
              className="mt-2 h-10 w-auto rounded border border-border bg-muted p-1"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Primary colour</Label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={wlPrimary}
                onChange={(e) => setWlPrimary(e.target.value)}
                className="h-9 w-12 cursor-pointer rounded-xl border border-border bg-background p-0.5"
              />
              <span className="text-sm font-mono text-muted-foreground">{wlPrimary}</span>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Accent colour</Label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={wlAccent}
                onChange={(e) => setWlAccent(e.target.value)}
                className="h-9 w-12 cursor-pointer rounded-xl border border-border bg-background p-0.5"
              />
              <span className="text-sm font-mono text-muted-foreground">{wlAccent}</span>
            </div>
          </div>
        </div>
      </div>

      <Button
        onClick={handleSave}
        disabled={saving}
        className="mt-5 rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
      >
        {saving ? "Saving..." : "Save branding"}
      </Button>
    </div>
  );
}
